/**
 * Workload Templates Settings
 *
 * Workspace library of GTM demand templates (phased hours by Resource Type
 * relative to a launch anchor). Assignment to priorities lives in the
 * Time-Phased Workbench — this page owns create / edit / delete only.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Rocket,
  Trash2,
  X,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import {
  getLaunchProfiles,
  seedBuiltinLaunchProfiles,
  upsertLaunchProfile,
  deleteLaunchProfile,
} from '../../../lib/timePhasedPlanningService';
import type { LaunchProfile, LaunchProfilePhase } from '../../../lib/timePhasedPlanning.types';
import { canonicalizeResourceType } from '../../../lib/timePhasedPlanning.types';
import { getItemCountState } from '../../../components/collection/types';

function emptyPhase(resourceType: string): LaunchProfilePhase {
  return {
    id: crypto.randomUUID().slice(0, 8),
    label: '',
    resourceType,
    offsetStartWeeks: -4,
    offsetEndWeeks: 0,
    hours: 40,
  };
}

function ProfileCard({
  profile,
  onEdit,
  onDelete,
}: {
  profile: LaunchProfile;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] m-0">
            {profile.name}
          </h3>
          {profile.description && (
            <p className="text-xs text-[var(--text-ter)] mt-1 mb-0">{profile.description}</p>
          )}
          <ul className="mt-3 space-y-1 text-xs text-[var(--text-sec)]">
            {profile.phases.map((ph) => (
              <li key={ph.id}>
                <span className="font-medium">{ph.label || ph.resourceType}</span>
                <span className="text-[var(--text-ter)]">
                  {' '}
                  · {canonicalizeResourceType(ph.resourceType)} · {ph.hours}h · wk{' '}
                  {ph.offsetStartWeeks}…{ph.offsetEndWeeks}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--bg-raised)]"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileEditor({
  initial,
  resourceTypes,
  onCancel,
  onSave,
}: {
  initial?: LaunchProfile;
  resourceTypes: { key: string; label: string }[];
  onCancel: () => void;
  onSave: (input: { name: string; description?: string; phases: LaunchProfilePhase[] }) => Promise<void>;
}) {
  const defaultRt = resourceTypes[0]?.key ?? 'engineering';
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [phases, setPhases] = useState<LaunchProfilePhase[]>(
    initial?.phases?.length
      ? initial.phases.map((p) => ({ ...p, resourceType: canonicalizeResourceType(p.resourceType) }))
      : [emptyPhase(defaultRt)],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updatePhase = (id: string, patch: Partial<LaunchProfilePhase>) => {
    setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (phases.length === 0) {
      setError('Add at least one phase');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        phases: phases.map((p) => ({
          ...p,
          resourceType: canonicalizeResourceType(p.resourceType),
          label: p.label.trim() || p.resourceType,
        })),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-primary-200 dark:border-primary-800/40 bg-[var(--bg-raised)] p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] m-0">
          {initial ? 'Edit workload template' : 'New workload template'}
        </h3>
        <button type="button" onClick={onCancel} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-medium text-[var(--text-sec)]">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full text-sm border border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)]"
          />
        </label>
        <label className="block text-xs font-medium text-[var(--text-sec)]">
          Description
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full text-sm border border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)]"
          />
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-[var(--text-sec)]">
            Phases (hours by Resource Type vs launch week 0)
          </span>
          <button
            type="button"
            onClick={() => setPhases((prev) => [...prev, emptyPhase(defaultRt)])}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600"
          >
            <Plus className="h-3.5 w-3.5" /> Add phase
          </button>
        </div>
        <div className="space-y-2">
          {phases.map((ph) => (
            <div
              key={ph.id}
              className="grid grid-cols-2 md:grid-cols-[1fr_1.2fr_80px_70px_70px_32px] gap-2 items-center"
            >
              <input
                value={ph.label}
                onChange={(e) => updatePhase(ph.id, { label: e.target.value })}
                placeholder="Label"
                className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
              />
              <select
                value={canonicalizeResourceType(ph.resourceType)}
                onChange={(e) => updatePhase(ph.id, { resourceType: e.target.value })}
                className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
              >
                {resourceTypes.map((rt) => (
                  <option key={rt.key} value={rt.key}>
                    {rt.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                value={ph.hours}
                onChange={(e) => updatePhase(ph.id, { hours: Number(e.target.value) })}
                className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
                aria-label="Hours"
              />
              <input
                type="number"
                value={ph.offsetStartWeeks}
                onChange={(e) => updatePhase(ph.id, { offsetStartWeeks: Number(e.target.value) })}
                className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
                aria-label="Start week offset"
              />
              <input
                type="number"
                value={ph.offsetEndWeeks}
                onChange={(e) => updatePhase(ph.id, { offsetEndWeeks: Number(e.target.value) })}
                className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
                aria-label="End week offset"
              />
              <button
                type="button"
                onClick={() => setPhases((prev) => prev.filter((p) => p.id !== ph.id))}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                aria-label="Remove phase"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[var(--text-ter)] mt-2">
          Columns: label · resource type · hours · start wk · end wk (relative to launch)
        </p>
      </div>

      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleSave()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-sm font-medium border border-[var(--rule)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function LaunchProfilesSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId ?? '');
  const { load: loadResourceTypes, getEnabledTypes } = useResourceTypesStore();
  const enabledTypes = getEnabledTypes();

  const [profiles, setProfiles] = useState<LaunchProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState<LaunchProfile | 'new' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      await loadResourceTypes(workspaceId);
      setProfiles(await getLaunchProfiles(workspaceId));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, loadResourceTypes]);

  useEffect(() => {
    void load();
  }, [load]);

  const countState = getItemCountState(profiles.length);
  const resourceOptions = useMemo(
    () =>
      enabledTypes.length > 0
        ? enabledTypes.map((t) => ({ key: t.key, label: t.label }))
        : [
            { key: 'engineering', label: 'Engineering' },
            { key: 'marketing', label: 'Marketing' },
            { key: 'legal', label: 'Legal' },
            { key: 'design', label: 'Design' },
            { key: 'support', label: 'Support' },
            { key: 'finance', label: 'Finance' },
          ],
    [enabledTypes],
  );

  const handleSeed = async () => {
    if (!workspaceId) return;
    setSeeding(true);
    try {
      const list = await seedBuiltinLaunchProfiles(workspaceId);
      setProfiles(list);
      setMessage('Built-in workload templates ready.');
    } finally {
      setSeeding(false);
    }
  };

  const handleSave = async (input: {
    name: string;
    description?: string;
    phases: LaunchProfilePhase[];
  }) => {
    if (!workspaceId) return;
    const saved = await upsertLaunchProfile(workspaceId, {
      id: editing && editing !== 'new' ? editing.id : undefined,
      ...input,
    });
    setProfiles((prev) => {
      const idx = prev.findIndex((p) => p.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    setEditing(null);
    setMessage(`Saved “${saved.name}”.`);
  };

  const handleDelete = async (profile: LaunchProfile) => {
    if (!confirm(`Delete workload template “${profile.name}”? Priorities using it will fall back to assumed demand.`)) {
      return;
    }
    await deleteLaunchProfile(profile.id);
    setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
    setMessage(`Deleted “${profile.name}”.`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      <PageHeader
        title="Workload Templates"
        subtitle="GTM demand templates: which teams need how many hours in which weeks relative to a launch. Assign profiles to priorities in the Time-Phased Workbench. Resource types come from Settings → Resource Types."
        icon={<Rocket className="h-4 w-4 text-primary-600" />}
      />

      <div className="border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 px-4 py-3 text-xs text-[var(--text-sec)] flex items-start gap-2">
        <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          This is the workspace <strong>hour-template library</strong>. It is not{' '}
          <strong>Launch Types</strong> (marketing classification on items — Settings → Planning),
          Work Types (how a kind of work is approached), or Work Profiles (resource type on a work unit).
        </span>
      </div>

      {message && (
        <p className="text-sm text-[var(--text-sec)]">{message}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={seeding || !workspaceId}
          onClick={() => void handleSeed()}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] disabled:opacity-50"
        >
          {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Seed built-in templates
        </button>
        <button
          type="button"
          disabled={!workspaceId || editing !== null}
          onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          New profile
        </button>
      </div>

      {editing !== null && (
        <ProfileEditor
          initial={editing === 'new' ? undefined : editing}
          resourceTypes={resourceOptions}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      ) : countState === 'zero' ? (
        <div className="border border-dashed border-[var(--rule)] p-8 text-center text-sm text-[var(--text-ter)]">
          No workload templates yet. Seed the built-in templates or create a custom one.
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              onEdit={() => setEditing(p)}
              onDelete={() => void handleDelete(p)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
